// SurahBrowser — Hisni Phase 1a.
// Renders all 114 surahs as a filterable grid with per-surah mastery progress.
// Phase 1a: standalone component inside AWENI (no old Memoriz deps).
//
// Data sources:
//   - Chapter metadata : GET https://api.quran.com/api/v4/chapters?language=<locale>
//     (public API, no auth required; cache:'no-store' because Cloudflare's
//     edge caches the first-requested locale and returns it for all subsequent
//     requests regardless of the ?language= query param — no-store bypasses
//     this and ensures the correct locale is always fetched from origin)
//   - User progress     : GET /aweni/api/v1/gateway/memoriz/api/surahs/progress
//     (requires JWT from ctx.auth.getToken(); guest mode falls back to all-zero)
import type { AweniContext } from '../../types';

// ─── Data shapes ────────────────────────────────────────────────────────────

interface Chapter {
  id: number;
  name_simple: string;
  name_arabic: string;
  name_complex: string;
  verses_count: number;
  translated_name?: { name: string };
}

interface SurahProgress {
  surah: number;
  percentage: number;
  memorized?: number;
  totalAyahs?: number;
}

type FilterKey = 'all' | 'started' | 'completed';

// ─── Component ───────────────────────────────────────────────────────────────

export class SurahBrowser {
  private readonly container: HTMLElement;
  private readonly ctx: AweniContext;
  private readonly onSelect: (surahNumber: number) => void;

  private chapters: Chapter[] = [];
  private progressMap: Map<number, SurahProgress> = new Map();
  private activeFilter: FilterKey = 'all';
  private readonly isGuest: boolean;

  constructor(
    container: HTMLElement,
    ctx: AweniContext,
    onSelect: (surahNumber: number) => void,
  ) {
    this.container = container;
    this.ctx = ctx;
    this.onSelect = onSelect;
    this.isGuest = !ctx.auth.getToken();
    this._init();
  }

  private async _init(): Promise<void> {
    injectStyles();
    this._renderSkeleton();

    const locale = this.ctx.i18n.locale || 'en';
    const token = this.ctx.auth.getToken();

    const [chaptersResult, progressResult] = await Promise.allSettled([
      fetchChapters(locale),
      token ? fetchProgress(token) : Promise.resolve<SurahProgress[]>([]),
    ]);

    if (chaptersResult.status === 'fulfilled') {
      this.chapters = chaptersResult.value;
    }
    if (progressResult.status === 'fulfilled') {
      for (const p of progressResult.value) {
        this.progressMap.set(p.surah, p);
      }
    }

    this._render();
  }

  private _filteredChapters(): Chapter[] {
    const chapters =
      this.activeFilter === 'started'
        ? this.chapters.filter(c => {
            const p = this.progressMap.get(c.id);
            return p != null && p.percentage > 0 && p.percentage < 100;
          })
        : this.activeFilter === 'completed'
          ? this.chapters.filter(c => {
              const p = this.progressMap.get(c.id);
              return p != null && p.percentage >= 100;
            })
          : this.chapters;
    return chapters;
  }

  private _renderSkeleton(): void {
    this.container.innerHTML = `
      <div class="hisni-sb">
        <div class="hisni-sb-skeleton">
          ${Array(8).fill('<div class="hisni-sb-card-skeleton"></div>').join('')}
        </div>
      </div>`;
  }

  private _render(): void {
    const t = (k: string, fb: string) => this.ctx.i18n.t(k) || fb;
    const isRTL = this.ctx.i18n.dir === 'rtl';
    const filtered = this._filteredChapters();

    // In guest mode only the 'all' tab is shown.
    const tabs: Array<{ id: FilterKey; label: string }> = [
      { id: 'all', label: t('hisni_sb_all', 'All') },
      ...(this.isGuest
        ? []
        : [
            { id: 'started' as FilterKey, label: t('hisni_sb_started', 'Started') },
            { id: 'completed' as FilterKey, label: t('hisni_sb_completed', 'Completed') },
          ]),
    ];

    this.container.innerHTML = `
      <div class="hisni-sb" dir="${isRTL ? 'rtl' : 'ltr'}">
        <div class="hisni-sb-filters" role="tablist" aria-label="${escHtml(t('hisni_sb_all', 'Surah filter'))}">
          ${tabs
            .map(
              tab => `
            <button
              class="hisni-sb-tab${this.activeFilter === tab.id ? ' hisni-sb-tab--active' : ''}"
              role="tab"
              aria-selected="${this.activeFilter === tab.id}"
              data-filter="${tab.id}">
              ${escHtml(tab.label)}
            </button>`,
            )
            .join('')}
        </div>
        <div class="hisni-sb-grid" role="list">
          ${
            filtered.length === 0
              ? `<p class="hisni-sb-empty">${escHtml(t('hisni_sb_no_results', 'No surahs match your filter.'))}</p>`
              : filtered
                  .map((c, i) => this._renderCard(c, i, isRTL, t))
                  .join('')
          }
        </div>
      </div>`;

    // Wire tab clicks
    this.container.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        const next = btn.dataset.filter as FilterKey;
        if (this.activeFilter === next) return;
        this.activeFilter = next;
        this._render();
      });
    });

    // Wire surah card clicks + keyboard activation
    this.container.querySelectorAll<HTMLElement>('[data-surah]').forEach(card => {
      const handler = () => {
        const n = parseInt(card.dataset.surah ?? '0', 10);
        if (n > 0) this.onSelect(n);
      };
      card.addEventListener('click', handler);
      card.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
      });
    });
  }

  private _renderCard(
    chapter: Chapter,
    index: number,
    isRTL: boolean,
    _t: (k: string, fb: string) => string,
  ): string {
    const p = this.progressMap.get(chapter.id);
    const pct = p?.percentage ?? 0;
    const pctRounded = Math.round(pct);
    const translatedName = chapter.translated_name?.name ?? '';

    // Design-token colour based on mastery level.
    const progressColor =
      pct >= 100
        ? 'var(--ln-accent-ceremonial)'
        : pct >= 1
          ? 'var(--ln-accent)'
          : 'var(--ln-text-secondary)';

    // RTL: progress fills from right → left.
    const progressOrigin = isRTL ? 'right' : 'left';

    const pctLabel =
      this.ctx.i18n.t('hisni_sb_progress_pct', { pct: pctRounded }) ||
      `${pctRounded}% memorized`;

    return `
      <div
        class="hisni-sb-card"
        role="listitem"
        tabindex="0"
        data-surah="${chapter.id}"
        aria-label="${escHtml(chapter.name_simple + ' — ' + pctLabel)}"
        style="--item-index:${index}">
        <div class="hisni-sb-card-num" aria-hidden="true">${chapter.id}</div>
        <div class="hisni-sb-card-names">
          <div class="hisni-sb-card-arabic" lang="ar">${escHtml(chapter.name_arabic)}</div>
          <div class="hisni-sb-card-latin">${escHtml(chapter.name_simple)}</div>
          ${translatedName ? `<div class="hisni-sb-card-trans">${escHtml(translatedName)}</div>` : ''}
        </div>
        <div class="hisni-sb-card-meta">
          <div class="hisni-sb-card-verses" aria-hidden="true">${chapter.verses_count}</div>
          ${pct > 0 ? `<div class="hisni-sb-pct-badge" style="color:${progressColor}">${pctRounded}%</div>` : ''}
        </div>
        <div class="hisni-sb-bar-track" aria-hidden="true">
          <div
            class="hisni-sb-bar-fill"
            style="transform:scaleX(${pct / 100});transform-origin:${progressOrigin};background:${progressColor};"
          ></div>
        </div>
      </div>`;
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}

// ─── Fetch helpers ───────────────────────────────────────────────────────────

async function fetchChapters(locale: string): Promise<Chapter[]> {
  const res = await fetch(
    `https://api.quran.com/api/v4/chapters?language=${encodeURIComponent(locale)}`,
    { cache: 'no-store' },
  );
  if (!res.ok) throw new Error(`Quran API HTTP ${res.status}`);
  const json: unknown = await res.json();
  const root = json as Record<string, unknown>;
  return Array.isArray(root.chapters) ? (root.chapters as Chapter[]) : [];
}

async function fetchProgress(token: string): Promise<SurahProgress[]> {
  const res = await fetch('/aweni/api/v1/gateway/memoriz/api/surahs/progress', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Progress API HTTP ${res.status}`);
  const json: unknown = await res.json();
  if (Array.isArray(json)) return json as SurahProgress[];
  const root = json as Record<string, unknown>;
  return Array.isArray(root.data) ? (root.data as SurahProgress[]) : [];
}

// ─── Styles ──────────────────────────────────────────────────────────────────

function injectStyles(): void {
  if (document.getElementById('hisni-sb-styles')) return;
  const s = document.createElement('style');
  s.id = 'hisni-sb-styles';
  s.textContent = `
    /* ── Keyframe (reduced-motion safe) ──────────────────────────── */
    @keyframes hisni-fade-in {
      from { opacity: 0; translate: 0 6px; }
      to   { opacity: 1; translate: none; }
    }
    @media (prefers-reduced-motion: reduce) {
      .hisni-sb-card { animation: none !important; }
    }

    /* ── Shell ───────────────────────────────────────────────────── */
    .hisni-sb {
      padding: var(--ln-space-2, 8px) 0;
    }

    /* ── Filter chips (chip-row pattern) ─────────────────────────── */
    .hisni-sb-filters {
      display: flex;
      gap: 6px;
      padding: 0 var(--ln-space-3, 12px) var(--ln-space-2, 8px);
      overflow-x: auto;
      scrollbar-width: none;
    }
    .hisni-sb-filters::-webkit-scrollbar { display: none; }

    .hisni-sb-tab {
      flex-shrink: 0;
      padding: 5px 14px;
      border: 1px solid var(--ln-border, rgba(0,0,0,.12));
      border-radius: var(--ln-radius-full, 9999px);
      background: transparent;
      color: var(--ln-text-secondary);
      font-size: .8rem;
      font-weight: 600;
      cursor: pointer;
      transition:
        background var(--motion-duration-fast, 120ms) ease,
        color var(--motion-duration-fast, 120ms) ease,
        border-color var(--motion-duration-fast, 120ms) ease;
    }
    .hisni-sb-tab--active {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: transparent;
    }
    .hisni-sb-tab:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }

    /* ── Card grid ───────────────────────────────────────────────── */
    .hisni-sb-grid {
      display: grid;
      gap: 6px;
      padding: 0 var(--ln-space-3, 12px);
    }

    .hisni-sb-card {
      position: relative;
      display: grid;
      grid-template-columns: 36px 1fr auto;
      grid-template-rows: auto 3px;
      align-items: center;
      gap: 0 10px;
      padding: 10px 12px 0;
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,.08));
      border-radius: var(--ln-radius-md, 12px);
      cursor: pointer;
      overflow: hidden;
      /* Stagger animation */
      animation: hisni-fade-in var(--motion-duration-normal, 200ms) var(--motion-easing-standard, ease) both;
      animation-delay: calc(var(--motion-stagger-default, 60ms) * min(var(--item-index), 8));
    }
    .hisni-sb-card:hover {
      border-color: var(--ln-accent, #0A0A0A);
    }
    .hisni-sb-card:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }

    .hisni-sb-card-num {
      grid-column: 1;
      grid-row: 1;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--ln-radius-sm, 6px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.04));
      font-size: .75rem;
      font-weight: 700;
      color: var(--ln-text-secondary);
      flex-shrink: 0;
    }

    .hisni-sb-card-names {
      grid-column: 2;
      grid-row: 1;
      min-width: 0;
      padding-bottom: 10px;
    }
    .hisni-sb-card-arabic {
      font-size: 1rem;
      font-weight: 700;
      color: var(--ln-text-primary, #111);
      line-height: 1.3;
      font-family: var(--font-arabic, 'Amiri', serif);
    }
    .hisni-sb-card-latin {
      font-size: .82rem;
      font-weight: 600;
      color: var(--ln-text-primary, #222);
    }
    .hisni-sb-card-trans {
      font-size: .72rem;
      color: var(--ln-text-secondary);
    }

    .hisni-sb-card-meta {
      grid-column: 3;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      padding-bottom: 10px;
    }
    .hisni-sb-card-verses {
      font-size: .7rem;
      color: var(--ln-text-secondary);
    }
    .hisni-sb-pct-badge {
      font-size: .75rem;
      font-weight: 700;
    }

    /* ── Progress bar (Motion spec B — transform:scaleX, no width%) ─ */
    .hisni-sb-bar-track {
      grid-column: 1 / -1;
      grid-row: 2;
      height: 3px;
      background: var(--ln-border, rgba(0,0,0,.08));
      margin: 0 -12px;
      overflow: hidden;
    }
    .hisni-sb-bar-fill {
      width: 100%;
      height: 100%;
      transition:
        transform var(--motion-duration-normal, 200ms) var(--motion-easing-standard, ease);
      will-change: transform;
    }

    /* ── Empty state ─────────────────────────────────────────────── */
    .hisni-sb-empty {
      padding: var(--ln-space-4, 16px) var(--ln-space-3, 12px);
      text-align: center;
      color: var(--ln-text-secondary);
      font-size: .85rem;
    }

    /* ── Skeleton loading ────────────────────────────────────────── */
    .hisni-sb-skeleton {
      display: grid;
      gap: 6px;
      padding: 0 var(--ln-space-3, 12px);
    }
    .hisni-sb-card-skeleton {
      height: 66px;
      border-radius: var(--ln-radius-md, 12px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.06));
      animation: hisni-sb-pulse var(--motion-duration-deliberate) var(--motion-easing-default) infinite;
    }
    @keyframes hisni-sb-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: .45; }
    }
    @media (prefers-reduced-motion: reduce) {
      .hisni-sb-card-skeleton { animation: none; }
    }

    /* ── Dark mode ───────────────────────────────────────────────── */
    .dark .hisni-sb-card {
      background: rgba(255,255,255,.04);
      border-color: rgba(255,255,255,.10);
    }
    .dark .hisni-sb-card:hover { border-color: var(--ln-accent, #C8A96A); }
    .dark .hisni-sb-card-num { background: rgba(255,255,255,.08); }
    .dark .hisni-sb-bar-track { background: rgba(255,255,255,.08); }
    .dark .hisni-sb-card-skeleton { background: rgba(255,255,255,.08); }
  `;
  document.head.appendChild(s);
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
